'use client';
import { JsonForms } from '@jsonforms/react';
import { useEffect, useRef, useState } from 'react';
// import { materialRenderers } from '@jsonforms/material-renderers';
import {
  addEventParam,
  INinjaEvent,
  resetPostHog,
  useEventStore,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import {
  KissanEventType,
  useAppPersistenceStore,
  useCropPreferenceStore,
  useGlobalLoginContext,
  useProfileStore,
  useResumableFeedStore,
} from '@ninjacart/nc-nk-store';
import { Button, Spinner, Stack, Text, useToastController, View } from '@ninjacart/nc-nk-ui';
import { usePathname, useRouter } from 'next/navigation';
import { GestureResponderEvent, Platform } from 'react-native';
import { useParams, useSearchParams } from 'solito/navigation';
import ErrorBanner from './errorBanner';
import { materialRenderers } from './materialRenderers';
import { JSONFormSchema } from './types/types';

declare global {
  interface Window {
    ReactNativeWebView: any;
  }
}

type DynamicFormQueryParams = {
  entityType?: string;
  entityId?: string;
  productType?: string;
  skipSplash?: string;
  userName?: string;
  phoneNumber?: string;
  pinCode?: string;
  accessToken?: string;
};

export default function DynamicFormClient({
  name,
  schema,
  uiSchema,
  id,
  // title = "Ninja Kisan Leads"
}: JSONFormSchema) {
  const searchParams = useSearchParams<DynamicFormQueryParams>();
  const entityType = searchParams?.get('entityType');
  const entityId = searchParams?.get('entityId');
  const productType = searchParams?.get('productType');
  const skipSplash = searchParams?.get('skipSplash');
  const autoLogout = searchParams?.get('autoLogout');
  // const name = searchParams?.get('name');
  // const phoneNumber = searchParams?.get('phoneNumber');
  // const pinCode = searchParams?.get('pinCode');
  // const accessToken = searchParams?.get('accessToken');
  const baseUri = process.env.NEXT_PUBLIC_APP_WORKFLOW_URI_V2;
  const router = useRouter();
  const [data, setData] = useState<object>({});
  const [hasError, setHasError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const toast = useToastController();
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  console.log('Schema => ', searchParams?.get('entityId'));
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const fields = useGlobalLoginContext(['navigationParams']);
  const setNavigationParams = fields.navigationParams.set;
  const setUserSkippedLogin = useProfileStore((state) => state.setUserSkippedLogin);
  const clearStorage = useProfileStore((state) => state.clearStorage);
  const clearCropPreferenceStorage = useCropPreferenceStore(
    (state) => state.clearCropPreferenceStorage
  );
  const resetResumableFeedStore = useResumableFeedStore((state) => state.resetResumableFeedStore);
  const setOnLoginRedirect = useAppPersistenceStore((state) => state.setOnLoginRedirect);
  const setAdId = useAppPersistenceStore((state) => state.setAdId);
  const pathname = usePathname();
  const params = useParams();
  // forms/1?entityId=990254a4-6c91-405d-ac60-cb93cbc3f986&entityType=product&productType=machinery
  useEffect(() => {
  if (!hasUserLoggedIn() && pathname.startsWith('/forms')) {
    setNavigationParams({
      name: '',
      routeName: {
        name: `forms/${params?.id}`,
        params: {
          ...(entityId && { entityId }),
          ...(entityType && { entityType }),
          ...(productType && { productType }),
          ...(autoLogout && { autoLogout }),
        },
      },
      description: ``,
      screenName: 'lead form validation',
    });
    router.push('/login');
    return;
  }
}, []);

  if (!schema) {
    return <div>Error loading form schema.</div>;
  }

  const clearAllData = () => {
    useEventStore.getState()?.clearEventUserDetails();
    resetPostHog();
    setUserSkippedLogin(false);
    clearStorage();
    clearCropPreferenceStorage();
    resetResumableFeedStore();
    setOnLoginRedirect(undefined);
    router.push('/login');
    setAdId('');
  };

  const dynamizeFormData = () => {
    const props = schema.properties;
    const data: any = {};
    for (const key in props) {
      const value = searchParams?.get(key);
      if (value) {
        data[key] = isNumeric(value) ? Number(value) : value;
      }
    }
    setData(data);
  };

  const isNumeric = (value: string) => {
    return /^-?\d+$/.test(value);
  };

  useEffect(() => {
    captureFormEvent(KissanEventType.PRODUCT_LEADCAPTURE);
    dynamizeFormData();
  }, []);

  const submitFormData = async (data: object): Promise<boolean> => {
    try {
      setLoading(true);
      setHasError(false);
      captureFormEvent(KissanEventType.PRODUCT_LEADCAPTURE_SUBMIT, data);
      const url = `${baseUri}/kisaan/forms/p/realms/realm123/users/1/form/${id}/data`;

      const postData = {
        entityId,
        entityType,
        data,
      };
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(postData),
      });

      if (!response.ok) {
        return false;
      }

      setHasError(false);
      setIsSubmitted(true);
      setData({});
      return true;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }

    return false;
  };

  // const handleSubmit = async (event: GestureResponderEvent) => {
  //   const submit = await submitFormData(data);

  //   if (!submit) {
  //     // window.ReactNativeWebView?.['postMessage']?.({ event: JSON.stringify(data) });
  //     setHasError(true);
  //     return;
  //   }

  //   if (typeof window !== 'undefined' && window?.ReactNativeWebView) {
  //     window.ReactNativeWebView?.['postMessage']?.(JSON.stringify({ event: 'GO_BACK' }));
  //   }
  //   else{
  //     window.postMessage(JSON.stringify({ event: 'GO_BACK' }), '*');
  //      toast.show('Submitted Successfully!!!', { duration: 3000 });
  //     router.back();
  //   }

  //   event.preventDefault();
  //   event.stopPropagation();
  // };

  const handleSubmit = async (event: GestureResponderEvent) => {
    const submit = await submitFormData(data);

    if (!submit) {
      setHasError(true);
      return;
    }

    if (autoLogout == 'true' && pathname.startsWith('/forms')) {
      clearAllData();
      return
    }

 
    if (typeof window !== 'undefined' && Platform.OS === 'web') {
      const message = JSON.stringify({ event: 'GO_BACK' });

      if (!window.ReactNativeWebView) {
        window.parent.postMessage(message, window.location.origin);
        return;
      } else {
        window.ReactNativeWebView.postMessage(message);
      }
    }
    
    event.preventDefault();
    event.stopPropagation();
  };
  const captureFormEvent = (eventName: string, jsonData?: object) => {
    const additionalDetails = [
      addEventParam('itemId', entityId || undefined),
      addEventParam('productType', productType || undefined),
      addEventParam('formId', id),
    ];

    if (jsonData) {
      additionalDetails.push(addEventParam('formData', JSON.stringify(jsonData)));
    }

    const likeEvent: INinjaEvent = {
      name: eventName,
      screenDetails: { screen: 'lead capture page web' },
      eventParams: additionalDetails,
    };

    enqueue(likeEvent);
  };

  return (
    <View background={'#FFF'} flex={1} padding="$4">
      <Stack>
        <Text color={'#000'} marginBottom="$4" fontSize={24} fontWeight={600}>
          {name || 'Ninja Kisan Leads'}
        </Text>
        {isSubmitted && (
          <View marginTop="$2" marginBottom="$2">
            <ErrorBanner message={'Submitted successfully!!!'} backgroundColor="#04AA6D" />
          </View>
        )}
        <JsonForms
          schema={schema}
          uischema={uiSchema}
          data={data}
          renderers={materialRenderers}
          onChange={({ data }) => {
            setData(data);
            if (Object.keys(data).length !== 0) {
              setIsSubmitted(false);
            }

            if (JSON.stringify(data).length % 20 === 0) {
              captureFormEvent(KissanEventType.PRODUCT_LEADCAPTURE_TYPING, data);
            }
          }}
          validationMode="ValidateAndShow"
        />

        {!isSubmitted && (
          <Button
            size="$4"
            backgroundColor={'#04AA6D'}
            color={'whitesmoke'}
            marginTop={12}
            theme="active"
            onPress={handleSubmit}
          >
            Submit
          </Button>
        )}
        <Button
          variant="outlined"
          marginTop={12}
          color={'#000'}
          onPress={(e) => {
            e.preventDefault();
            if (typeof window !== 'undefined') {
              const message = JSON.stringify({ event: 'GO_BACK_CANCEL' });

              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(message);
              } else if (window) {
                window.postMessage(message, '*');
                router.back();
              }
            }
          }}
        >
          {isSubmitted ? 'Go Back' : 'Cancel'}
        </Button>
        {pathname.startsWith('/forms') && (
          <Button
            variant="outlined"
            marginTop={12}
            onPress={(e) => {
              e.preventDefault();
              clearAllData();
            }}
          >
            {'Logout'}
          </Button>
        )}

        {hasError && <ErrorBanner message={''} backgroundColor="#f44336" />}
      </Stack>
      {loading && (
        <Stack
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor="rgba(0,0,0,0.5)"
          justifyContent="center"
          alignItems="center"
          borderRadius="$4"
          zIndex={10} // Ensure it appears above the form
        >
          <Spinner size="large" color="white" />
          <Text color="white" marginTop="$2">
            Submitting...
          </Text>
        </Stack>
      )}
    </View>
  );
}
